import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import ProviderTracker from '../pages/ProviderTracker';
import CounselorTracker from '../pages/CounselorTracker';
import NurseTracker from '../pages/NurseTracker';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../css/PatientTrackerV2.css';

/**
 * Patient Tracker V2 — redesigned shell.
 * Reuses the existing Provider/Counselor/Nurse tracker views unchanged;
 * the v2 look is applied purely through PatientTrackerV2.css, scoped under
 * the `.ptv2` wrapper, so the original /BillerPatientTracker page is untouched.
 */
const PatientTrackerTabs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('');
  const [secondaryInsData] = useState([]);

  const basePath = '/BillerPatientTracker';

  const shellRef = useRef(null);

  /**
   * Publishes how far down the document this page starts, as --ptv2-top.
   *
   * The page is sized `calc(100dvh - that)` so it fits the screen exactly and
   * scrolls its table rather than itself. The offset cannot be a constant: the
   * global header re-wraps to a second row as the window narrows, so the page
   * starts at one height on a wide screen and a taller one on a narrow screen.
   *
   * Measured in a layout effect so the height is right before the first paint,
   * otherwise the page renders a viewport too tall for one frame and flashes a
   * scrollbar.
   */
  useLayoutEffect(() => {
    const el = shellRef.current;
    if (!el || typeof window === 'undefined') return undefined;

    let frame = 0;

    const measure = () => {
      frame = 0;
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY));
      // Only write on a real change. The observer below watches <body>, whose
      // height this value decides - rewriting it every time would feed itself.
      if (el.dataset.ptv2Top === String(top)) return;
      el.dataset.ptv2Top = String(top);
      el.style.setProperty('--ptv2-top', `${top}px`);
    };

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    measure();

    window.addEventListener('resize', schedule);

    // resize alone misses the header re-wrapping, which changes the offset
    // without changing the window size.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (observer) observer.observe(document.body);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const currentTab = location.pathname.split('/').pop();
    setActiveTab(currentTab);

    if (!currentTab || location.pathname === basePath) {
      navigate(`${basePath}/Provider`, { replace: true });
    }
  }, [location, navigate]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    navigate(`${basePath}/${tab}`);
  };

  const tabs = [
    { key: 'Provider', label: 'Provider' },
    { key: 'counselor', label: 'Counselor' },
    { key: 'nurse', label: 'Nurse' },
  ];

  return (
    <div className="ptv2" ref={shellRef}>

      <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} newestOnTop theme="colored" />

      <div className="ptv2-tabs" id="pills-tab" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`ptv2-tab ${activeTab === tab.key ? 'active' : ''}`}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => handleTabClick(tab.key)}
          >
            {tab.label}
            {tab.key !== 'Provider' && secondaryInsData.length > 0 && (
              <span className="ptv2-tab-flag">{secondaryInsData.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="tab-content" id="pills-tabContent">
        <Routes>
          <Route path="Provider" element={<ProviderTracker/>} />
          <Route path="counselor" element={<CounselorTracker/>} />
          <Route path="nurse" element={<NurseTracker />} />
        </Routes>
      </div>
    </div>
  )
}

export default PatientTrackerTabs
